import type { ChatEntryWithSubTables } from "../../shared/aiSdkMessages";
import { CLIENT_AUTO_RESOLVED_TOOLS } from "../../shared/clientAutoResolvedTools";

function isConversationalEntry(entry: { entryType: string }) {
  return entry.entryType !== "Compaction";
}

export type StatusState =
  | "waiting"
  | "building"
  | "thinking"
  | "loading"
  | "resting";

export function getStatusState(
  entries: ChatEntryWithSubTables[],
  isResponding: boolean,
  runStatus: "idle" | "running",
  awaitingAction: boolean
): StatusState {
  // Meta entries appended while the chat is idle must
  // not mask the conversational state (e.g. an askQuestion still awaiting an
  // answer would stop reading as "waiting").
  const lastEntry = entries.findLast(isConversationalEntry);
  if (!lastEntry) return "resting";
  if (lastEntry.entryType === "UserInterrupt") return "resting";
  // "Waiting" is driven by the server-set awaitingAction flag (the same signal
  // the workflow list uses), not a client guess from an incomplete tool call.
  // A tool that auto-resolves server-side (e.g. connectToApp) never sets it, so
  // its in-flight window can't flash the connect prompt.
  if (lastEntry.entryType === "AssistantToolUse" && awaitingAction)
    return "waiting";
  if (
    lastEntry.entryType === "AssistantToolUse" &&
    lastEntry.toolCall &&
    lastEntry.toolCall.completedAt == null &&
    CLIENT_AUTO_RESOLVED_TOOLS.has(lastEntry.toolCall.toolName)
  )
    return "building";
  if (runStatus === "running") {
    if (lastEntry.entryType === "AssistantToolUse") return "building";
    if (lastEntry.entryType === "AssistantMessage") return "building";
    return "thinking";
  }
  if (lastEntry.entryType === "UserMessage")
    return isResponding ? "thinking" : "resting";
  if (
    lastEntry.entryType === "AssistantReasoning" &&
    !lastEntry.assistantReasoning?.signature
  )
    return "thinking";
  if (lastEntry.entryType === "AssistantMessage" && isResponding)
    return "thinking";
  if (lastEntry.entryType === "AssistantToolUse" && isResponding)
    return "building";
  if (isResponding) return "loading";
  return "resting";
}
